import { SlideDeckStatus } from '@breakoutlearning/firebase-repository/models/SlideDeck'
import {
  AssignmentState,
  AssignmentStatus,
  LibraryObjectState,
  SectionState,
  SlideDeckType,
} from '@breakoutlearning/firebase-repository/types'
import { SectionStatus } from '@breakoutlearning/firebase-repository/models/Section'
import classNames from 'classnames'
import { XSmallIcon } from 'components/icons/XSmall'
import { useSettings } from 'hooks/settings'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useTranslationTyped } from 'i18n/i18n'

function PillDot({
  className = 'bg-fixed-grey',
  pulsing = false,
}: {
  className?: string
  pulsing?: boolean
}) {
  const { animationsEnabled } = useSettings()
  const shouldPulse = animationsEnabled && pulsing
  const animation = shouldPulse ? 'pulse-animation' : ''
  return (
    <span
      data-testid="pill-dot"
      className={classNames(
        'inline-block h-[8px] w-[8px] rounded-full',
        className,
        animation
      )}
    ></span>
  )
}

export function BreakoutPill({
  icon,
  size = 'small',
  kind = 'secondary',
  disabled,
  dot,
  dotPulsing,
  dotClassName,
  label,
  chip,
  className,
  style,
  onClick,
  role,
  element,
  'aria-label': ariaLabel,
  'aria-live': ariaLive,
}: {
  kind?: 'primary' | 'secondary' | 'tertiary' | 'transparent' | 'accent'
  size?: 'small' | 'large' | 'tiny'
  icon?: React.ReactNode
  label: string
  disabled?: boolean
  dot?: boolean
  dotClassName?: string
  dotPulsing?: boolean
  chip?: boolean
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
  role?: string
  element?: 'span' | 'button'
  'aria-label'?: string
  'aria-live'?: 'off' | 'assertive' | 'polite'
}) {
  const { tt } = useTranslationTyped()
  // We're using strong because of a11y insanity
  const Element = element || 'strong'
  return (
    <Element
      aria-live={ariaLive}
      aria-label={ariaLabel}
      role={role}
      style={style}
      onClick={onClick}
      className={classNames(
        'inline-flex items-center justify-center gap-1 px-4 py-1 text-label-medium',
        className,
        {
          'cursor-pointer': chip !== undefined,
          'bg-core-tertiary': kind === 'primary',
          'bg-core-secondary': kind === 'secondary',
          'border border-outline-light bg-transparent': kind === 'tertiary',
          'bg-transparent': kind === 'transparent',
          'bg-fixed-accent-color': kind === 'accent',
          'rounded-full': size === 'small',
          'h-13 min-w-38 rounded-2xl': size === 'large',
          'h-4 min-w-2 rounded-xl px-2! text-xxs': size === 'tiny',
        },
        // Handle text color separately since disabled styles make it more complex
        {
          'text-core-on-tertiary': !disabled && kind === 'primary',
          'text-core-on-secondary':
            !disabled &&
            (kind === 'secondary' ||
              kind === 'tertiary' ||
              kind === 'transparent'),
          'text-on-surface-var': disabled,
        }
      )}
    >
      {dot || dotClassName ? (
        <PillDot className={dotClassName} pulsing={dotPulsing} />
      ) : null}
      {icon ? <span className="inline">{icon}</span> : null}
      {label}
      {chip && (
        <button
          className="border-none"
          onClick={onClick}
          aria-label={tt.a11y.remove_item({ item: label })}
        >
          <XSmallIcon size={12} className="text-core-error" />
        </button>
      )}
    </Element>
  )
}

BreakoutPill.LibraryObjectState = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    state: LibraryObjectState
  }
) {
  switch (props.state) {
    case LibraryObjectState.live:
      return <BreakoutPill.Live {...props} />
    case LibraryObjectState.invited:
      return <BreakoutPill.Invited {...props} />
    case LibraryObjectState.mustSchedule:
      return <BreakoutPill.MustSchedule {...props} />
    case LibraryObjectState.completed:
      return <BreakoutPill.Completed {...props} />
    case LibraryObjectState.enrolled:
      return <BreakoutPill.Enrolled {...props} />
    case LibraryObjectState.expired:
      return <BreakoutPill.Expired {...props} />
    case LibraryObjectState.canceled:
      return <BreakoutPill.Canceled {...props} />
    case LibraryObjectState.pending:
      return <BreakoutPill.Pending {...props} />
    case LibraryObjectState.readyToStart:
      return <BreakoutPill.ReadyToStart {...props} />
    case LibraryObjectState.scheduled:
      return <BreakoutPill.Scheduled {...props} />
    case LibraryObjectState.suspended:
      return <BreakoutPill.Suspended {...props} />
  }
}
BreakoutPill.SectionState = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    state: SectionState
  }
) {
  switch (props.state) {
    case SectionState.notStarted:
      return <BreakoutPill.NotStarted {...props} />
    case SectionState.completed:
      return <BreakoutPill.Completed {...props} />
    case SectionState.inProgress:
      return <BreakoutPill.InProgress {...props} />
    case SectionState.unconfigured:
      return <BreakoutPill.NotStarted {...props} />
  }
}

BreakoutPill.SlideDeckStatus = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    status: SlideDeckStatus
  }
) {
  switch (props.status) {
    case SlideDeckStatus.archived:
      return <BreakoutPill.Archived {...props} />
    case SlideDeckStatus.draft:
      return <BreakoutPill.Draft {...props} />
    case SlideDeckStatus.hidden:
      return <BreakoutPill.Archived {...props} />
    case SlideDeckStatus.published:
      return <BreakoutPill.Published {...props} />
  }
}

BreakoutPill.SlideDeckType = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    type: SlideDeckType
    plural: boolean
  }
) {
  const { tt } = useTranslationTyped()
  let dotClassName: string | undefined
  switch (props.type) {
    case SlideDeckType.original:
      dotClassName = 'bg-black'
      break
    case SlideDeckType.dialog:
      dotClassName = 'bg-fixed-vibrant-green'
      break
    case SlideDeckType.simulation:
      dotClassName = 'bg-fixed-rust'
      break
    case SlideDeckType.negotiation:
      dotClassName = 'bg-fixed-accent-color'
      break
    case SlideDeckType.demo:
      dotClassName = 'bg-fixed-teal'
      break
    case SlideDeckType.oral_exam:
      dotClassName = 'bg-fixed-orange'
      break
    case SlideDeckType.tutorial:
      dotClassName = 'bg-fixed-powder-blue'
      break
  }

  const root = props.plural
    ? tt.admin_details.experience_types_plural
    : tt.admin_details.experience_types

  return (
    <BreakoutPill
      {...props}
      label={root[SlideDeckType[props.type] as keyof typeof root]()}
      dotClassName={dotClassName}
    />
  )
}

BreakoutPill.AssignmentStatus = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    state: AssignmentStatus
  }
) {
  switch (props.state) {
    case AssignmentStatus.draft:
      return <BreakoutPill.Draft {...props} />
    case AssignmentStatus.pending:
      return <BreakoutPill.Pending {...props} />
    case AssignmentStatus.in_progress:
      return <BreakoutPill.InProgress {...props} />
    case AssignmentStatus.completed:
      return <BreakoutPill.Completed {...props} />
    case AssignmentStatus.canceled:
      return <BreakoutPill.Canceled {...props} />
    case AssignmentStatus.unconfigured:
      return <BreakoutPill.Unconfigured {...props} />
  }
}

BreakoutPill.AssignmentState = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    state: AssignmentState
  }
) {
  switch (props.state) {
    case AssignmentState.unconfigured:
      return <BreakoutPill.Unconfigured {...props} />
    case AssignmentState.draft:
      return <BreakoutPill.Draft {...props} />
    case AssignmentState.active:
      return <BreakoutPill.Active {...props} />
    case AssignmentState.canceled:
      return <BreakoutPill.Canceled {...props} />
  }
}

type PrefabPillProps = {
  kind?: 'primary' | 'secondary' | 'tertiary' | 'transparent'
  size?: 'small' | 'large'
  disabled?: boolean
  className?: string
  dotClassName?: string
}

BreakoutPill.Chips = observer(function BreakoutPillPrefab(
  props: PrefabPillProps & {
    kind?: 'primary' | 'secondary'
    labels: string[]
    onClick: (label: string) => void
  }
) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {props.labels.map((label, index) => (
        <BreakoutPill.Chip
          kind={props.kind || 'secondary'}
          key={index}
          onClick={() => props.onClick(label)}
          label={label}
        />
      ))}
    </div>
  )
})

BreakoutPill.Chip = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    label: string
    onClick: () => void
  }
) {
  return <BreakoutPill {...props} chip />
}

BreakoutPill.Live = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.live')}
      dotClassName={classNames('bg-fixed-vibrant-green', props.dotClassName)}
      dotPulsing
    />
  )
}

BreakoutPill.MustSchedule = function BreakoutPillPrefab(
  props: PrefabPillProps
) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.must_schedule')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.Enrolled = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return <BreakoutPill {...props} label={t('pills.enrolled')} dot />
}

BreakoutPill.Pending = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.pending')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.ReadyToStart = function BreakoutPillPrefab(
  props: PrefabPillProps
) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.ready_to_start')}
      dotClassName={classNames('bg-fixed-vibrant-green', props.dotClassName)}
      dotPulsing
    />
  )
}

BreakoutPill.Scheduled = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.scheduled')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.Suspended = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.suspended')}
      dotClassName={classNames('bg-fixed-accent-color', props.dotClassName)}
    />
  )
}

BreakoutPill.CompletedPendingPeerReview = function BreakoutPillPrefab(
  props: PrefabPillProps
) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.needs_peer_review')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.Invited = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.invited')}
      dotClassName={classNames('bg-fixed-grey', props.dotClassName)}
    />
  )
}

BreakoutPill.NotStarted = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.not_started')}
      dotClassName={classNames('bg-fixed-grey', props.dotClassName)}
    />
  )
}

BreakoutPill.InProgress = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.in_progress')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.Completed = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.completed')}
      dotClassName={classNames('bg-core-success', props.dotClassName)}
    />
  )
}

BreakoutPill.Expired = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.expired')}
      dotClassName={classNames('bg-core-error', props.dotClassName)}
    />
  )
}

BreakoutPill.Canceled = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.canceled')}
      dotClassName={classNames('bg-core-error', props.dotClassName)}
    />
  )
}

BreakoutPill.Abandoned = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.abandoned')}
      dotClassName={classNames('bg-core-error', props.dotClassName)}
    />
  )
}

BreakoutPill.Draft = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.draft')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.Archived = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.archived')}
      dotClassName={classNames('bg-core-error', props.dotClassName)}
    />
  )
}

BreakoutPill.Published = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.published')}
      dotClassName={classNames('bg-core-success', props.dotClassName)}
    />
  )
}

BreakoutPill.Unconfigured = function BreakoutPillPrefab(
  props: PrefabPillProps
) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.unconfigured')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.Draft = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.draft')}
      dotClassName={classNames('bg-fixed-orange', props.dotClassName)}
    />
  )
}

BreakoutPill.Active = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.active')}
      dotClassName={classNames('bg-core-success', props.dotClassName)}
    />
  )
}

BreakoutPill.Canceled = function BreakoutPillPrefab(props: PrefabPillProps) {
  const { t } = useTranslation()
  return (
    <BreakoutPill
      {...props}
      label={t('pills.canceled')}
      dotClassName={classNames('bg-core-error', props.dotClassName)}
    />
  )
}

BreakoutPill.Admin = function BreakoutPillPrefab(props: {
  label: string
  onClick: () => void
  className?: string
}) {
  return (
    <BreakoutPill
      {...props}
      className={classNames(
        'border-1 border-fuchsia-500 text-fuchsia-500',
        props.className
      )}
    />
  )
}

BreakoutPill.SectionStatus = function BreakoutPillPrefab(
  props: PrefabPillProps & {
    status: SectionStatus
  }
) {
  const { tt } = useTranslationTyped()
  let dotClassName: string
  switch (props.status) {
    case SectionStatus.draft:
    case SectionStatus.invoiceRejected:
      dotClassName = 'bg-core-error'
      break
    case SectionStatus.notStarted:
      dotClassName = 'bg-fixed-grey'
      break
    case SectionStatus.inProgress:
      dotClassName = 'bg-fixed-orange'
      break
    case SectionStatus.completed:
      dotClassName = 'bg-core-success'
      break
    case SectionStatus.invoicePending:
      dotClassName = 'bg-breakout-orange'
      break
  }
  return (
    <BreakoutPill
      {...props}
      label={tt.instructor_classes.section_status[props.status]()}
      dotClassName={classNames(dotClassName, props.dotClassName)}
    />
  )
}
